import React, { useState, useEffect } from 'react';
import { Eye, Edit, Save, X, Plus, Package, Layers, AlertCircle, Calendar, User, FileText, Trash2 } from 'lucide-react';
import { useApp } from '../App';
import { pendingOrdersService, ordersService } from '../services/api';

const ControlPanel = () => {
  const { db, setDb } = useApp();
  const [activeTab, setActiveTab] = useState('pedidos-pendientes');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // Estado para el buscador

  // Estado para formularios de edición
  const [editFormData, setEditFormData] = useState({});
  const [editItems, setEditItems] = useState([]);
  
  // Helper para parsear fechas sin conversión de zona horaria
  const parseDateWithoutTimezone = (dateStr) => {
    if (!dateStr) return null;
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  };
  
  // Helper para formatear fechas
  const formatDate = (dateStr, format = 'es-CO') => {
    if (!dateStr) return 'N/A';
    const date = parseDateWithoutTimezone(dateStr);
    if (format === 'es-ES-long') {
      return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
    }
    return date.toLocaleDateString(format, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  
  // Escuchar eventos de actualización de pedidos
  useEffect(() => {
    const handleOrderUpdate = async (event) => {
      console.log('🔄 Orden actualizada, recargando datos...');
      // Recargar órdenes desde el backend
      try {
        const boskoOrders = await ordersService.getOrders('bosko');
        const luminaOrders = await ordersService.getOrders('lumina');
        setDb(prevDb => ({
          ...prevDb,
          boskoOrders,
          luminaOrders
        }));
      } catch (error) {
        console.error('Error recargando órdenes:', error);
      }
    };

    window.addEventListener('orderUpdated', handleOrderUpdate);
    return () => window.removeEventListener('orderUpdated', handleOrderUpdate);
  }, []);

  // Obtener datos por pestaña
  // Enriquecer pedidos pendientes con campos de visualización
  const enrichPendingOrder = (order) => {
    return {
      ...order,
      salaVenta: order.salaVenta || order.cliente || order.customer,
      nombreAsesor: order.nombreAsesor,
      numeroFactura: order.numeroFactura,
      fechaEntrega: order.fechaEntrega || order.fecha_entrega,
      descripcionEntrega: order.descripcionEntrega || order.producto || order.observaciones,
      customer: order.salaVenta || order.cliente || order.customer,
      status: order.status || order.estado || 'pendiente'
    };
  };

  const getOrdersByTab = (tab) => {
    let orders = [];
    
    switch(tab) {
      case 'pedidos-pendientes':
        orders = (db.pendingOrders || []).map(order => enrichPendingOrder({ ...order, module: 'PENDIENTE', moduleColor: 'text-orange-600', moduleBg: 'bg-orange-100' }));
        break;
      case 'bosko':
        orders = db.boskoOrders.map(order => ({ ...order, module: 'BOSKO', moduleColor: 'text-green-600', moduleBg: 'bg-green-100' }));
        break;
      case 'lumina':
        orders = db.luminaOrders.map(order => ({ ...order, module: 'LUMINA', moduleColor: 'text-gray-600', moduleBg: 'bg-gray-100' }));
        break;
      default:
        orders = [];
    }
    
    // Filtrar por búsqueda de factura
    if (searchQuery.trim()) {
      orders = orders.filter(order => {
        const facturaNumber = order.numeroFactura || '';
        return facturaNumber.toLowerCase().includes(searchQuery.toLowerCase());
      });
    }
    
    return orders;
  };

  const getTabStats = (tab) => {
    const orders = getOrdersByTab(tab);
    return {
      total: orders.length,
      pending: orders.filter(o => (o.status || o.estado || o.estadoEntrega) === 'pendiente').length,
      inProgress: orders.filter(o => (o.status || o.estado || o.estadoEntrega) === 'en-proceso').length,
      completed: orders.filter(o => (o.status || o.estado || o.estadoEntrega) === 'entregado').length,
      ready: orders.filter(o => (o.status || o.estado || o.estadoEntrega) === 'listo').length
    };
  };

  const openEditModal = (order) => {
    console.log('🔄 Abriendo modal de edición para:', order);
    
    setEditingOrder(order);
    
    // Para BOSKO y LUMINA, cargar los items
    if (order.module === 'BOSKO' || order.module === 'LUMINA') {
      setEditItems(order.items || []);
      setEditFormData({
        id: order._id || order.id,
        client: order.client || order.customer || '',
        numeroFactura: order.numeroFactura || '',
        fechaVenta: order.fechaVenta ? order.fechaVenta.split('T')[0] : '',
        fechaEntrega: order.fechaEntrega ? order.fechaEntrega.split('T')[0] : '',
        status: order.status || 'pendiente'
      });
    } else {
      // Para pedidos pendientes
      setEditFormData({
        id: order.id,
        salaVenta: order.salaVenta || order.cliente || order.customer || '',
        nombreAsesor: order.nombreAsesor || '',
        fechaEntrega: (order.fechaEntrega || order.fecha_entrega) ? (order.fechaEntrega || order.fecha_entrega).split('T')[0] : '',
        numeroFactura: order.numeroFactura || '',
        descripcionEntrega: order.descripcionEntrega || order.producto || order.observaciones || '',
        status: order.status || order.estado || order.estadoEntrega || 'pendiente'
      });
    }
    setShowEditModal(true);
  };

  const deleteOrder = async (order) => {
    if (!window.confirm(`¿Estás seguro de eliminar este registro de ${order.client || order.salaVenta || 'este pedido'}?`)) return;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      let url, stateKey;
      if (order.module === 'PENDIENTE') {
        url = `${API_URL}/api/pending-orders/${order.id || order._id}`;
        stateKey = 'pendingOrders';
      } else {
        url = `${API_URL}/api/orders/${order.id || order._id}`;
        stateKey = order.module === 'BOSKO' ? 'boskoOrders' : 'luminaOrders';
      }
      const user = JSON.parse(localStorage.getItem('clenip_user') || '{}');
      const token = user.access_token || '';
      const response = await fetch(url, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const orderId = order.id || order._id;
        setDb(prev => ({
          ...prev,
          [stateKey]: (prev[stateKey] || []).filter(o => (o.id || o._id) !== orderId)
        }));
      } else {
        alert('Error al eliminar el registro');
      }
    } catch (err) {
      console.error('Error eliminando:', err);
      alert('Error de conexión al eliminar');
    }
  };

  const saveOrderChanges = async () => {
    console.log('🔄 Actualizando pedido...', editFormData);

    try {
      // Para BOSKO y LUMINA
      if (editingOrder.module === 'BOSKO' || editingOrder.module === 'LUMINA') {
        if (!editFormData.client?.trim()) {
          alert('Por favor ingrese el nombre del cliente');
          return;
        }
        
        const orderData = {
          client: editFormData.client,
          product: editItems.map(item => 
            editingOrder.module === 'BOSKO' 
              ? `${item.descripcion} (${item.color})` 
              : `${item.descripcion} (${item.grosorVidrio})`
          ).join(', '),
          total: editingOrder.total || 0,
          date: new Date().toISOString(),
          type: editingOrder.module.toLowerCase(),
          numeroFactura: editFormData.numeroFactura,
          fechaVenta: editFormData.fechaVenta,
          fechaEntrega: editFormData.fechaEntrega,
          items: editItems,
          status: editFormData.status
        };

        await ordersService.updateOrder(editFormData.id, orderData);
        
        // Recargar órdenes desde el backend
        const orders = await ordersService.getOrders(editingOrder.module.toLowerCase());
        
        if (editingOrder.module === 'BOSKO') {
          setDb(prevDb => ({ ...prevDb, boskoOrders: orders }));
        } else {
          setDb(prevDb => ({ ...prevDb, luminaOrders: orders }));
        }
        
        // Emitir evento global para sincronización
        window.dispatchEvent(new CustomEvent('orderUpdated', { 
          detail: { type: editingOrder.module.toLowerCase(), id: editFormData.id } 
        }));
        
      } else if (editingOrder.module === 'PENDIENTE') {
        // Para pedidos pendientes
        if (!editFormData.salaVenta?.trim()) {
          alert('Por favor ingrese el nombre de la sala de venta');
          return;
        }
        
        const backendOrder = {
          cliente: editFormData.salaVenta,
          telefono: editingOrder.telefono || '',
          producto: editFormData.descripcionEntrega || editingOrder.producto,
          precio: editFormData.total || 0,
          anticipo: editingOrder.anticipo || 0,
          fecha_entrega: editFormData.fechaEntrega,
          observaciones: editFormData.descripcionEntrega || editingOrder.observaciones,
          estado: editFormData.status,
          numeroFactura: editFormData.numeroFactura,
          nombreAsesor: editFormData.nombreAsesor || editingOrder.nombreAsesor
        };

        await pendingOrdersService.updatePendingOrder(editingOrder.id, backendOrder);
        
        // Recargar pedidos pendientes
        const pendingOrders = await pendingOrdersService.getPendingOrders();
        setDb(prevDb => ({ ...prevDb, pendingOrders }));
      }

      setShowEditModal(false);
      setEditingOrder(null);
      alert('✅ Pedido actualizado exitosamente');

    } catch (error) {
      console.error('❌ Error actualizando pedido:', error);
      alert('❌ Error al actualizar el pedido: ' + error.message);
    }
    
    // Notificación de éxito
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-blue-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 flex items-center gap-2';
    notification.innerHTML = `
      <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
      </svg>
      <span>✅ Pedido actualizado exitosamente</span>
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 3000);
  };

  const handleInputChange = (field, value) => {
    console.log(`🔧 ControlPanel - Campo ${field} cambiado a:`, value);
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const updateItem = (index, field, value) => {
    setEditItems(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };
  
  const removeItem = (index) => {
    setEditItems(prev => prev.filter((_, i) => i !== index));
  };
  
  const addNewItem = () => {
    if (editingOrder.module === 'BOSKO') {
      setEditItems(prev => [...prev, {
        descripcion: '',
        color: 'Blanco',
        ancho: '',
        largo: '',
        alto: ''
      }]);
    } else if (editingOrder.module === 'LUMINA') {
      setEditItems(prev => [...prev, {
        descripcion: '',
        grosorVidrio: '3mm',
        ancho: '',
        alto: '',
        observaciones: ''
      }]);
    }
  };

  const viewOrderDetails = (order) => {
    console.log('👁️ ControlPanel - Visualizando detalles del pedido:', order);
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const getStatusColor = (status) => {
    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '_');
    switch(normalizedStatus) {
      case 'entregado': 
        return 'bg-green-100 text-green-800';
      case 'pendiente': 
        return 'bg-yellow-100 text-yellow-800';
      case 'en_proceso': 
      case 'en-proceso':
        return 'bg-blue-100 text-blue-800';
      case 'listo_para_entrega':
      case 'listo-para-entrega':
      case 'listo':
        return 'bg-purple-100 text-purple-800';
      default: 
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusText = (status) => {
    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '_');
    switch(normalizedStatus) {
      case 'entregado': 
        return 'Entregado';
      case 'pendiente': 
        return 'Pendiente';
      case 'en_proceso': 
      case 'en-proceso':
        return 'En Proceso';
      case 'listo_para_entrega':
      case 'listo-para-entrega':
      case 'listo':
        return 'Listo para Entrega';
      default: 
        return 'Pendiente';
    }
  };

  const calculateDaysOverdue = (fechaEntrega) => {
    if (!fechaEntrega) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reiniciar a medianoche
    
    // Parsear la fecha sin conversión de zona horaria
    const deliveryDate = parseDateWithoutTimezone(fechaEntrega);
    
    const diffTime = today - deliveryDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : null;
  };

  const renderOrdersTable = (orders) => {
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left p-4 text-gray-600 font-medium">
                {activeTab === 'pedidos-pendientes' ? 'Sala de Venta' : 'Cliente'}
              </th>
              <th className="text-left p-4 text-gray-600 font-medium">Factura</th>
              <th className="text-left p-4 text-gray-600 font-medium">Fecha Entrega</th>
              <th className="text-left p-4 text-gray-600 font-medium">Estado</th>
              <th className="text-center p-4 text-gray-600 font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center p-12 text-gray-500">
                  <div className="flex flex-col items-center gap-3">
                    <FileText size={48} className="text-gray-300" />
                    <span className="text-lg">No hay pedidos en esta categoría</span>
                  </div>
                </td>
              </tr>
            ) : (
              orders.map(order => {
                const daysOverdue = calculateDaysOverdue(order.fechaEntrega);
                return (
                  <tr key={order.id} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="p-4">
                      <span className="font-medium">{order.client || order.customer || 'N/A'}</span>
                    </td>
                    <td className="p-4 text-gray-600">{order.numeroFactura || 'N/A'}</td>
                    <td className="p-4">
                      <div className="flex flex-col">
                        <span className="text-gray-800">
                          {formatDate(order.fechaEntrega)}
                        </span>
                        {daysOverdue && (
                          <span className="text-xs text-red-600 font-medium">
                            {daysOverdue} día{daysOverdue !== 1 ? 's' : ''} de retraso
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                        {getStatusText(order.status)}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => viewOrderDetails(order)}
                          className="text-blue-500 hover:text-blue-700 transition-colors p-1 rounded hover:bg-blue-50"
                          title="Ver detalles"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => openEditModal(order)}
                          className="text-green-500 hover:text-green-700 transition-colors p-1 rounded hover:bg-green-50"
                          title="Editar pedido"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => deleteOrder(order)}
                          className="text-red-500 hover:text-red-700 transition-colors p-1 rounded hover:bg-red-50"
                          title="Eliminar registro"
                          data-testid={`delete-order-${order.id || order._id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const currentOrders = getOrdersByTab(activeTab);
  const stats = getTabStats(activeTab);

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FileText className="text-blue-500" size={32} />
            Panel de Control
          </h2>
          <p className="text-gray-600 mt-2">
            Gestión centralizada de todos los pedidos por categoría
          </p>
        </div>
      </div>

      {/* Buscador */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="🔍 Buscar por número de factura..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <svg
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              title="Limpiar búsqueda"
            >
              <X size={20} />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="text-sm text-gray-600 mt-2">
            Buscando: <span className="font-semibold">{searchQuery}</span> - {currentOrders.length} resultado(s)
          </p>
        )}
      </div>

      {/* Pestañas */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="flex gap-2 border-b mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('pedidos-pendientes')}
            className={`pb-3 px-4 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'pedidos-pendientes'
                ? 'text-orange-600 border-b-2 border-orange-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <AlertCircle size={18} />
            Pedidos Pendientes
          </button>
          <button
            onClick={() => setActiveTab('bosko')}
            className={`pb-3 px-4 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'bosko'
                ? 'text-green-600 border-b-2 border-green-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Package size={18} />
            BOSKO
          </button>
          <button
            onClick={() => setActiveTab('lumina')}
            className={`pb-3 px-4 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'lumina'
                ? 'text-gray-600 border-b-2 border-gray-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Layers size={18} />
            LUMINA
          </button>
        </div>

        {/* Estadísticas por pestaña */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className={`${
            activeTab === 'pedidos-pendientes' ? 'bg-orange-50' : 
            activeTab === 'bosko' ? 'bg-green-50' : 'bg-gray-50'
          } p-4 rounded-lg`}>
            <h3 className={`text-sm font-medium ${
              activeTab === 'pedidos-pendientes' ? 'text-orange-600' : 
              activeTab === 'bosko' ? 'text-green-600' : 'text-gray-600'
            } mb-1`}>
              Total
            </h3>
            <p className={`text-2xl font-bold ${
              activeTab === 'pedidos-pendientes' ? 'text-orange-700' : 
              activeTab === 'bosko' ? 'text-green-700' : 'text-gray-700'
            }`}>
              {stats.total}
            </p>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-yellow-600 mb-1">Pendientes</h3>
            <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-blue-600 mb-1">En Proceso</h3>
            <p className="text-2xl font-bold text-blue-700">{stats.inProgress}</p>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-green-600 mb-1">Completados</h3>
            <p className="text-2xl font-bold text-green-700">{stats.completed}</p>
          </div>
        </div>

        {/* Tabla de pedidos */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              Lista de Pedidos - {
                activeTab === 'pedidos-pendientes' ? 'Pedidos Pendientes' :
                activeTab === 'bosko' ? 'BOSKO' : 'LUMINA'
              }
            </h3>
            <div className="text-sm text-gray-500">
              Mostrando {currentOrders.length} pedido{currentOrders.length !== 1 ? 's' : ''}
            </div>
          </div>
          
          {renderOrdersTable(currentOrders)}
        </div>
      </div>

      {/* Modal de Edición */}
      {/* Modal de Edición - BOSKO */}
      {showEditModal && editingOrder && editingOrder.module === 'BOSKO' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-green-600">Editar Pedido BOSKO</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2">Cliente *</label>
                <input
                  type="text"
                  value={editFormData.client || ''}
                  onChange={(e) => handleInputChange('client', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Nombre del cliente"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Nº Factura *</label>
                <input
                  type="text"
                  value={editFormData.numeroFactura || ''}
                  onChange={(e) => handleInputChange('numeroFactura', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Número de factura"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Fecha de Venta *</label>
                <input
                  type="date"
                  value={editFormData.fechaVenta || ''}
                  onChange={(e) => handleInputChange('fechaVenta', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Fecha de Entrega *</label>
                <input
                  type="date"
                  value={editFormData.fechaEntrega || ''}
                  onChange={(e) => handleInputChange('fechaEntrega', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Estado *</label>
                <select
                  value={editFormData.status || 'pendiente'}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                >
                  <option value="pendiente">Pendiente</option>
                  <option value="en-proceso">En Proceso</option>
                  <option value="listo">Listo para Entrega</option>
                  <option value="entregado">Entregado</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-semibold">Ítems del Pedido</h4>
                <button
                  onClick={addNewItem}
                  className="bg-green-100 hover:bg-green-200 text-green-700 px-4 py-2 rounded-lg transition-colors"
                >
                  + Añadir Ítem
                </button>
              </div>
              
              {editItems.map((item, index) => (
                <div key={index} className="border rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-2">
                    <div>
                      <input
                        type="text"
                        value={item.descripcion}
                        onChange={(e) => updateItem(index, 'descripcion', e.target.value)}
                        className="w-full p-3 border rounded-lg"
                        placeholder="Descripción"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        value={item.color}
                        onChange={(e) => updateItem(index, 'color', e.target.value)}
                        className="w-full p-3 border rounded-lg"
                        placeholder="Color"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        value={item.ancho}
                        onChange={(e) => updateItem(index, 'ancho', e.target.value)}
                        className="w-full p-3 border rounded-lg"
                        placeholder="Ancho (cm)"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        value={item.largo}
                        onChange={(e) => updateItem(index, 'largo', e.target.value)}
                        className="w-full p-3 border rounded-lg"
                        placeholder="Largo (cm)"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        value={item.alto}
                        onChange={(e) => updateItem(index, 'alto', e.target.value)}
                        className="w-full p-3 border rounded-lg"
                        placeholder="Alto (cm)"
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    <Trash2 size={16} className="inline mr-1" />
                    Eliminar ítem
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <button
                onClick={saveOrderChanges}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg font-semibold transition-colors"
              >
                <Save size={20} className="inline mr-2" />
                Guardar Cambios
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-semibold transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición - LUMINA */}
      {showEditModal && editingOrder && editingOrder.module === 'LUMINA' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-600">Editar Pedido LUMINA</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2">Cliente *</label>
                <input
                  type="text"
                  value={editFormData.client || ''}
                  onChange={(e) => handleInputChange('client', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Nombre del cliente"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Nº Factura *</label>
                <input
                  type="text"
                  value={editFormData.numeroFactura || ''}
                  onChange={(e) => handleInputChange('numeroFactura', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Número de factura"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Fecha de Venta *</label>
                <input
                  type="date"
                  value={editFormData.fechaVenta || ''}
                  onChange={(e) => handleInputChange('fechaVenta', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Fecha de Entrega *</label>
                <input
                  type="date"
                  value={editFormData.fechaEntrega || ''}
                  onChange={(e) => handleInputChange('fechaEntrega', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Estado *</label>
                <select
                  value={editFormData.status || 'pendiente'}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full p-3 border rounded-lg"
                >
                  <option value="pendiente">Pendiente</option>
                  <option value="en-proceso">En Proceso</option>
                  <option value="listo">Listo para Entrega</option>
                  <option value="entregado">Entregado</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-semibold">Ítems del Pedido</h4>
                <button
                  onClick={addNewItem}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors"
                >
                  + Añadir Ítem
                </button>
              </div>
              
              {editItems.map((item, index) => (
                <div key={index} className="border rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2">
                    <div>
                      <input
                        type="text"
                        value={item.descripcion}
                        onChange={(e) => updateItem(index, 'descripcion', e.target.value)}
                        className="w-full p-3 border rounded-lg"
                        placeholder="Descripción"
                      />
                    </div>
                    <div>
                      <select
                        value={item.grosorVidrio}
                        onChange={(e) => updateItem(index, 'grosorVidrio', e.target.value)}
                        className="w-full p-3 border rounded-lg"
                      >
                        <option value="3mm">3mm</option>
                        <option value="4mm">4mm</option>
                        <option value="5mm">5mm</option>
                        <option value="6mm">6mm</option>
                        <option value="8mm">8mm</option>
                        <option value="10mm">10mm</option>
                      </select>
                    </div>
                    <div>
                      <input
                        type="text"
                        value={item.ancho}
                        onChange={(e) => updateItem(index, 'ancho', e.target.value)}
                        className="w-full p-3 border rounded-lg"
                        placeholder="Ancho (cm)"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        value={item.alto}
                        onChange={(e) => updateItem(index, 'alto', e.target.value)}
                        className="w-full p-3 border rounded-lg"
                        placeholder="Alto (cm)"
                      />
                    </div>
                  </div>
                  <div className="mb-2">
                    <input
                      type="text"
                      value={item.observaciones}
                      onChange={(e) => updateItem(index, 'observaciones', e.target.value)}
                      className="w-full p-3 border rounded-lg"
                      placeholder="Observaciones adicionales"
                    />
                  </div>
                  <button
                    onClick={() => removeItem(index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    <Trash2 size={16} className="inline mr-1" />
                    Eliminar ítem
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <button
                onClick={saveOrderChanges}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-semibold transition-colors"
              >
                <Save size={20} className="inline mr-2" />
                Guardar Cambios
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 bg-gray-400 hover:bg-gray-500 text-white py-3 rounded-lg font-semibold transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición - PENDIENTES (original) */}
      {showEditModal && editingOrder && editingOrder.module === 'PENDIENTE' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-orange-600">Editar Pedido Pendiente</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sala de Venta</label>
                  <input
                    type="text"
                    value={editFormData.salaVenta || ''}
                    onChange={(e) => handleInputChange('salaVenta', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Ej: Sala Principal, Sala Norte..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del Asesor</label>
                  <input
                    type="text"
                    value={editFormData.nombreAsesor || ''}
                    onChange={(e) => handleInputChange('nombreAsesor', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Nombre completo del asesor"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Número de Factura</label>
                  <input
                    type="text"
                    value={editFormData.numeroFactura || ''}
                    onChange={(e) => handleInputChange('numeroFactura', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Ej: FAC-001, PEND-123..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Entrega</label>
                  <input
                    type="date"
                    value={editFormData.fechaEntrega || ''}
                    onChange={(e) => handleInputChange('fechaEntrega', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                <select
                  value={editFormData.status || 'pendiente'}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="pendiente">Pendiente</option>
                  <option value="en-proceso">En Proceso</option>
                  <option value="listo">Listo para Entrega</option>
                  <option value="entregado">Entregado</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción de la Entrega</label>
                <textarea
                  value={editFormData.descripcionEntrega || ''}
                  onChange={(e) => handleInputChange('descripcionEntrega', e.target.value)}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
                  placeholder="Describe detalladamente qué se debe entregar, especificaciones, observaciones, etc."
                />
              </div>

              <div className="flex gap-4 mt-6">
                <button
                  onClick={saveOrderChanges}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <Save size={20} />
                  Guardar Cambios
                </button>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-semibold transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalles - BOSKO */}
      {showDetailsModal && selectedOrder && selectedOrder.module === 'BOSKO' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Header con nombre del cliente y estado */}
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-2">{selectedOrder.client || selectedOrder.customer || 'Sin nombre'}</h2>
                <p className="text-gray-500 text-lg">Factura: {selectedOrder.numeroFactura || 'N/A'}</p>
              </div>
              <div className="flex items-center gap-4">
                <span className={`px-6 py-3 rounded-2xl text-lg font-semibold ${
                  selectedOrder.status === 'pendiente' 
                    ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400' 
                    : selectedOrder.status === 'completado' || selectedOrder.status === 'entregado'
                    ? 'bg-green-100 text-green-800 border-2 border-green-400'
                    : selectedOrder.status === 'en-proceso'
                    ? 'bg-blue-100 text-blue-800 border-2 border-blue-400'
                    : selectedOrder.status === 'listo'
                    ? 'bg-purple-100 text-purple-800 border-2 border-purple-400'
                    : 'bg-gray-100 text-gray-800 border-2 border-gray-400'
                }`}>
                  {selectedOrder.status === 'pendiente' ? 'Pendiente' : 
                   selectedOrder.status === 'completado' || selectedOrder.status === 'entregado' ? 'Entregado' : 
                   selectedOrder.status === 'en-proceso' ? 'En Proceso' :
                   selectedOrder.status === 'listo' ? 'Listo para Entrega' : 'Pendiente'}
                </span>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-3xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Fechas */}
            <div className="bg-blue-50 rounded-2xl p-6 mb-6 grid grid-cols-2 gap-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-8 h-8 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-600 font-medium">Fecha de Venta</span>
                </div>
                <p className="text-2xl font-bold text-gray-800 ml-11">
                  {formatDate(selectedOrder.fechaVenta)}
                </p>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-8 h-8 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-600 font-medium">Fecha de Entrega</span>
                </div>
                <p className="text-2xl font-bold text-gray-800 ml-11">
                  {formatDate(selectedOrder.fechaEntrega)}
                </p>
              </div>
            </div>

            <hr className="my-6 border-gray-200" />

            {/* Ítems del Pedido */}
            <div className="mb-6">
              <h4 className="text-2xl font-bold text-gray-800 mb-6">Ítems del Pedido:</h4>
              {selectedOrder.items && selectedOrder.items.length > 0 ? (
                <div className="space-y-4">
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="bg-gray-50 rounded-xl p-6">
                      <div className="grid grid-cols-3 gap-6">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Descripción</p>
                          <p className="text-lg font-bold text-gray-800">{item.descripcion || 'Sin descripción'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Color</p>
                          <p className="text-lg font-bold text-gray-800">{item.color || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Dimensiones</p>
                          <p className="text-lg font-bold text-gray-800">
                            {(item.ancho || item.largo || item.alto) ? 
                              `${[item.ancho, item.largo, item.alto].filter(Boolean).join(' x ')} cm` : 
                              'N/A'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 p-8 rounded-xl text-center text-gray-500">
                  <Package size={48} className="mx-auto mb-2 text-gray-300" />
                  <p>No hay ítems registrados para este pedido</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalles - LUMINA */}
      {showDetailsModal && selectedOrder && selectedOrder.module === 'LUMINA' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Header con nombre del cliente y estado */}
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-2">{selectedOrder.client || selectedOrder.customer || 'Sin nombre'}</h2>
                <p className="text-gray-500 text-lg">Factura: {selectedOrder.numeroFactura || 'N/A'}</p>
              </div>
              <div className="flex items-center gap-4">
                <span className={`px-6 py-3 rounded-2xl text-lg font-semibold ${
                  selectedOrder.status === 'pendiente' 
                    ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400' 
                    : selectedOrder.status === 'completado' || selectedOrder.status === 'entregado'
                    ? 'bg-green-100 text-green-800 border-2 border-green-400'
                    : selectedOrder.status === 'en-proceso'
                    ? 'bg-blue-100 text-blue-800 border-2 border-blue-400'
                    : selectedOrder.status === 'listo'
                    ? 'bg-purple-100 text-purple-800 border-2 border-purple-400'
                    : 'bg-gray-100 text-gray-800 border-2 border-gray-400'
                }`}>
                  {selectedOrder.status === 'pendiente' ? 'Pendiente' : 
                   selectedOrder.status === 'completado' || selectedOrder.status === 'entregado' ? 'Entregado' : 
                   selectedOrder.status === 'en-proceso' ? 'En Proceso' :
                   selectedOrder.status === 'listo' ? 'Listo para Entrega' : 'Pendiente'}
                </span>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-3xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Fechas */}
            <div className="bg-purple-50 rounded-2xl p-6 mb-6 grid grid-cols-2 gap-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-8 h-8 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-600 font-medium">Fecha de Venta</span>
                </div>
                <p className="text-2xl font-bold text-gray-800 ml-11">
                  {formatDate(selectedOrder.fechaVenta)}
                </p>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-8 h-8 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-600 font-medium">Fecha de Entrega</span>
                </div>
                <p className="text-2xl font-bold text-gray-800 ml-11">
                  {formatDate(selectedOrder.fechaEntrega)}
                </p>
              </div>
            </div>

            <hr className="my-6 border-gray-200" />

            {/* Ítems del Pedido */}
            <div className="mb-6">
              <h4 className="text-2xl font-bold text-gray-800 mb-6">Ítems del Pedido:</h4>
              {selectedOrder.items && selectedOrder.items.length > 0 ? (
                <div className="space-y-4">
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="bg-gray-50 rounded-xl p-6">
                      <div className="grid grid-cols-4 gap-6">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Descripción</p>
                          <p className="text-lg font-bold text-gray-800">{item.descripcion || 'Sin descripción'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Grosor</p>
                          <p className="text-lg font-bold text-gray-800">{item.grosorVidrio || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Dimensiones</p>
                          <p className="text-lg font-bold text-gray-800">
                            {(item.ancho && item.alto) ? 
                              `${item.ancho} x ${item.alto} cm` : 
                              'N/A'
                            }
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Observaciones</p>
                          <p className="text-lg font-bold text-gray-800">{item.observaciones || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 p-8 rounded-xl text-center text-gray-500">
                  <Layers size={48} className="mx-auto mb-2 text-gray-300" />
                  <p>No hay ítems registrados para este pedido</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalles - PENDIENTES (Original) */}
      {showDetailsModal && selectedOrder && selectedOrder.module === 'PENDIENTE' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-2xl shadow-2xl border border-gray-200">
            {/* Header con botón cerrar */}
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800">
                Pedido #{selectedOrder.numeroFactura || selectedOrder.id || '000'}
              </h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-light"
              >
                ✕
              </button>
            </div>

            {/* Badges */}
            <div className="flex gap-4 mb-8">
              {/* Badge Principal - Sala de Venta */}
              <div className="bg-purple-600 text-white px-8 py-4 rounded-2xl font-bold text-lg">
                {selectedOrder.salaVenta || selectedOrder.cliente || selectedOrder.customer || 'SALA DE VENTA'}
              </div>
              
              {/* Badge Estado */}
              <div className={`px-6 py-4 rounded-2xl font-medium flex items-center gap-2 ${
                selectedOrder.status === 'pendiente' ? 'bg-yellow-100 text-yellow-700' :
                selectedOrder.status === 'en-proceso' ? 'bg-blue-100 text-blue-700' :
                selectedOrder.status === 'listo' ? 'bg-purple-100 text-purple-700' :
                selectedOrder.status === 'entregado' ? 'bg-green-100 text-green-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                <div className={`w-3 h-3 rounded-full ${
                  selectedOrder.status === 'pendiente' ? 'bg-yellow-500' :
                  selectedOrder.status === 'en-proceso' ? 'bg-blue-500' :
                  selectedOrder.status === 'listo' ? 'bg-purple-500' :
                  selectedOrder.status === 'entregado' ? 'bg-green-500' :
                  'bg-gray-500'
                }`}></div>
                {selectedOrder.status === 'pendiente' ? 'Pendiente' :
                 selectedOrder.status === 'en-proceso' ? 'En Proceso' :
                 selectedOrder.status === 'listo' ? 'Listo para Entrega' :
                 selectedOrder.status === 'entregado' ? 'Entregado' : 'Pendiente'}
              </div>
            </div>

            {/* Información Principal */}
            <div className="space-y-6">
              
              {/* Asesor */}
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <User size={20} className="text-gray-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm font-medium">Asesor</p>
                  <p className="text-gray-900 text-2xl font-bold">
                    {selectedOrder.nombreAsesor || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Descripción */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mt-1">
                  <FileText size={20} className="text-gray-600" />
                </div>
                <div className="flex-1">
                  <p className="text-gray-600 text-sm font-medium">Descripción</p>
                  <p className="text-gray-900 text-2xl font-bold leading-tight">
                    {selectedOrder.producto || selectedOrder.descripcionEntrega || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Fecha de Entrega */}
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <Calendar size={20} className="text-gray-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm font-medium">Fecha de Entrega</p>
                  <p className="text-gray-900 text-2xl font-bold">
                    {formatDate(selectedOrder.fechaEntrega || selectedOrder.fecha_entrega, 'es-ES-long')}
                  </p>
                </div>
              </div>
            </div>

            {/* Información Adicional (si existe) */}
            {(selectedOrder.observaciones || selectedOrder.producto || selectedOrder.descripcionEntrega) && (
              <div className="mt-8 p-4 bg-gray-50 rounded-xl">
                <p className="text-gray-600 text-sm font-medium mb-2">Observaciones</p>
                <p className="text-gray-900 text-2xl font-bold">
                  {selectedOrder.observaciones || selectedOrder.producto || selectedOrder.descripcionEntrega || 'Sin observaciones'}
                </p>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default ControlPanel;